import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useApp } from '../context/AppContext';
import { Plus, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminCategoriesPage = () => {
  const { getAuthHeaders } = useApp();
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: { ro: '', en: '', fr: '' },
    type: 'property_type',
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/categories`);
      setCategories(data);
    } catch (error) {
      toast.error('Failed to fetch categories');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/categories`, formData, {
        headers: getAuthHeaders(),
      });
      toast.success('Category created!');
      setFormData({ name: { ro: '', en: '', fr: '' }, type: 'property_type' });
      fetchCategories();
    } catch (error) {
      toast.error('Failed to create category');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/categories/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success('Category deleted!');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const groupedCategories = {
    property_type: categories.filter((c) => c.type === 'property_type'),
    area: categories.filter((c) => c.type === 'area'),
    feature: categories.filter((c) => c.type === 'feature'),
  };

  return (
    <AdminLayout>
      <div data-testid="admin-categories-page">
        <h1 className="text-4xl font-serif mb-8">Categories</h1>

        {/* Add Form */}
        <div className="glass p-6 mb-8">
          <h2 className="text-xl font-serif mb-4">Add Category</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Romanian"
                value={formData.name.ro}
                onChange={(e) => setFormData({ ...formData, name: { ...formData.name, ro: e.target.value } })}
                required
                className="bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
              />
              <input
                type="text"
                placeholder="English"
                value={formData.name.en}
                onChange={(e) => setFormData({ ...formData, name: { ...formData.name, en: e.target.value } })}
                required
                className="bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
              />
              <input
                type="text"
                placeholder="French"
                value={formData.name.fr}
                onChange={(e) => setFormData({ ...formData, name: { ...formData.name, fr: e.target.value } })}
                required
                className="bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
              />
            </div>
            <div className="flex gap-4">
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="flex-1 bg-slate-900 border border-white/20 py-2 px-3 text-sm"
              >
                <option value="property_type">Property Type</option>
                <option value="area">Area</option>
                <option value="feature">Feature</option>
              </select>
              <Button type="submit" data-testid="add-category-button">
                <Plus size={18} className="mr-2" />
                Add Category
              </Button>
            </div>
          </form>
        </div>

        {/* Categories List */}
        <div className="space-y-6">
          {Object.entries(groupedCategories).map(([type, items]) => (
            <div key={type}>
              <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-3">
                {type.replace('_', ' ')}
              </h3>
              <div className="glass p-4">
                {items.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No categories</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {items.map((cat) => (
                      <div
                        key={cat.id}
                        data-testid={`category-item-${cat.id}`}
                        className="flex items-center justify-between p-3 bg-slate-900/50 border border-white/5"
                      >
                        <div>
                          <span className="text-sm">{cat.name.en}</span>
                          <span className="text-xs text-muted-foreground ml-3">
                            ({cat.name.ro} / {cat.name.fr})
                          </span>
                        </div>
                        <Button
                          onClick={() => handleDelete(cat.id)}
                          data-testid={`delete-category-${cat.id}`}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCategoriesPage;